import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { Button, Dialog, Form, Toast } from "@sscs/ui";
import { convertBytesToUnit } from "@sscs/utils";
import { apiErrorToast } from "Utils";
import * as yup from "Utils/yup";
import { useEditSiteKeyMutation } from "../../services/licensing.api";
import { EditSiteKeyRequest, SiteKey } from "../../types";

type EditSiteKeyForm = Omit<EditSiteKeyRequest, "allocation_id">;

const editSiteKeySchema = (
  minAllocationInGB: number,
  allocationLimitInGB: number,
): yup.SchemaOf<EditSiteKeyForm> => {
  return yup.object().shape({
    description: yup.string().optional().max(500),
    allocation_bytes: yup
      .number()
      .required("Monthly allocation is required")
      .min(
        minAllocationInGB,
        `Monthly limit cannot be lower than the existing usage: ${minAllocationInGB.toFixed(2)} GB`,
      )
      .max(
        allocationLimitInGB,
        `Monthly limit cannot exceed the remaining available CLI capacity: ${allocationLimitInGB} GB`,
      ),
  });
};

type EditSiteKeyProps = {
  isOpen: boolean;
  siteKey: SiteKey;
  allocationLimit: number;
  onClose: () => void;
};

const EditSiteKeyContent = ({
  siteKey,
  allocationLimit,
  onClose,
}: Omit<EditSiteKeyProps, "isOpen">): React.JSX.Element => {
  const allocationLimitInGB = Math.floor(
    convertBytesToUnit(allocationLimit + siteKey.allocation_bytes, "GB"),
  );
  const methods = useForm<EditSiteKeyForm>({
    values: {
      description: siteKey.description,
      allocation_bytes: convertBytesToUnit(siteKey.allocation_bytes, "GB"),
    },
    resolver: yupResolver(
      editSiteKeySchema(
        convertBytesToUnit(siteKey.usage_bytes, "GB"),
        allocationLimitInGB,
      ),
    ),
  });
  const { control, formState, handleSubmit } = methods;

  const [editCliAllocation] = useEditSiteKeyMutation();

  const onSubmit: SubmitHandler<
    Omit<EditSiteKeyRequest, "allocation_id">
  > = async (data) => {
    try {
      await editCliAllocation({
        allocation_id: siteKey.id,
        description: data.description,
        allocation_bytes: data.allocation_bytes * 1024 * 1024 * 1024,
      }).unwrap();

      Toast.show({
        title: (
          <>
            Edited site key{" "}
            <strong>{siteKey.description ?? siteKey.site_key}</strong>
          </>
        ),
        type: "success",
      });
    } catch (error) {
      apiErrorToast(error, "Could not edit site key");
    }

    onClose();
  };

  return (
    <>
      <Dialog.Header>
        <Dialog.Header.Title>Edit site key</Dialog.Header.Title>
      </Dialog.Header>
      <FormProvider {...methods}>
        <Form.Form onSubmit={handleSubmit(onSubmit)}>
          <Dialog.Body>
            <Dialog.Section>
              <Dialog.Section.Content>
                <Form.TextField
                  name="description"
                  label="Description"
                  control={control}
                  errors={formState.errors}
                />
                <Form.TextField
                  name="allocation_bytes"
                  label="Monthly allocation (GB)"
                  type="number"
                  control={control}
                  errors={formState.errors}
                  unit="GB"
                  isRequired
                  inputProps={{
                    min: 0,
                    max: allocationLimitInGB,
                    step: 1,
                  }}
                  helperText={`Maximum ${allocationLimitInGB} GB`}
                />
              </Dialog.Section.Content>
            </Dialog.Section>
          </Dialog.Body>
          <Dialog.Footer>
            <Dialog.Footer.Actions>
              <Button color="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="primary">
                Edit site key
              </Button>
            </Dialog.Footer.Actions>
          </Dialog.Footer>
        </Form.Form>
      </FormProvider>
    </>
  );
};

const EditSiteKey = ({
  isOpen,
  ...props
}: EditSiteKeyProps): React.JSX.Element => (
  <Dialog.Frame isOpen={isOpen} onClose={props.onClose}>
    <EditSiteKeyContent {...props} />
  </Dialog.Frame>
);

export default EditSiteKey;
